import { useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { PageType } from '@vgtv/api-client/lib/page';

import { selectCategoryById } from '../../modules/categories';
import { getPath, routeNames } from '../../shared/helpers/routes';
import { isPlaybackValidAsset } from '../../shared/helpers/assetHelpers';
import { PageComponents } from '../../components/PageComponents/PageComponents';
import { pageStartsWithSplash } from '../../shared/helpers/pageHelpers';
import { PageMetadata } from '../../components/PageMetadata/PageMetadata';
import { PageSpinner } from '../../components/PageSpinner/PageSpinner';
import { usePageViewTracking } from '../../components/utils/usePageViewTracking';
import { replaceUrl } from '../../helpers/replaceUrl';
import { useAppSelector } from '../../modules/hooks';
import { PageTypeAdditional } from '../../types';
import { AssetPageStructuredData } from '../../components/StructuredData/AssetPageStructuredData';
import { useVendor } from '../../components/VendorPovider/VendorPovider';
import { usePageConfig } from '../usePageConfig';
import { useSvpAssetFetch } from '../useSvpAssetFetch';
import { NotFoundPage } from '../NotFoundPage';
import { useQuery } from '../../components/utils/useQuery';

import { AssetComponent } from './AssetComponent';

import type { SvpAsset } from '@vgtv/api-client/lib/svp_asset';
import type { FC } from 'react';

const AssetPage: FC = () => {
  const { assetId: assetIdParam } = useParams<{ assetId?: string }>();
  const { id } = useQuery<'id'>();
  const { vendor } = useVendor();

  const assetId = (assetIdParam || id) as string;

  const history = useHistory();

  const {
    asset,
    isFetched: isAssetFetched,
    isFetching: isAssetFetching,
  } = useSvpAssetFetch(parseInt(assetId) as SvpAsset['id'], vendor);

  const category = useAppSelector((state) =>
    selectCategoryById(state, vendor, asset && asset.category.id)
  );

  const { pageConfig } = usePageConfig({
    identifier: category && category.id,
    pageType: PageType.CATEGORY,
    vendor,
  });

  const components = useMemo(() => {
    if (!pageConfig) {
      return;
    }

    const { components } = pageConfig;

    return pageStartsWithSplash(pageConfig) ? components.slice(1) : components;
  }, [pageConfig]);

  useEffect(() => {
    if (asset && isPlaybackValidAsset(vendor, asset)) {
      // update url to contain the title short path
      replaceUrl(history, getPath(routeNames.asset, { asset }));
    }
  }, [asset, vendor, history]);

  usePageViewTracking({
    pageType: PageTypeAdditional.ASSET,
    entity: asset && isPlaybackValidAsset(vendor, asset) ? asset : undefined,
  });

  if (!asset) {
    if (!isAssetFetching && isAssetFetched) {
      return <NotFoundPage />;
    }

    return <PageSpinner />;
  }

  if (!isPlaybackValidAsset(vendor, asset)) {
    return <NotFoundPage />;
  }

  return (
    <div>
      <PageMetadata entity={asset} pageType={PageTypeAdditional.ASSET} />
      <AssetComponent asset={asset} />
      {pageConfig && <PageComponents componentsConfig={components!} />}
      <AssetPageStructuredData asset={asset} />
    </div>
  );
};

export { AssetPage };
